import * as React from "react"
import { Dot } from "lucide-react"

import { cn } from "@/lib/utils"

type InputOTPProps = React.HTMLAttributes<HTMLDivElement> & {
  value?: string
  onChange?: (value: string) => void
  maxLength?: number
  disabled?: boolean
  autoFocus?: boolean
  containerClassName?: string
  className?: string
}

const InputOTP = React.forwardRef<HTMLDivElement, InputOTPProps>(
  ({ value = "", onChange, maxLength = 6, disabled = false, autoFocus = false, className, containerClassName, ...props }, ref) => {
    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
      const nextValue = event.target.value.replace(/\D/g, "").slice(0, maxLength)
      onChange?.(nextValue)
    }

    const digits = value.slice(0, maxLength)
    const slots = Array.from({ length: maxLength }, (_, index) => digits[index] ?? "")

    return (
      <div ref={ref} className={cn("relative", containerClassName)} {...props}>
        <div className={cn("flex items-center gap-2", className)}>
          {slots.map((digit, index) => (
            <div
              key={index}
              className={cn(
                "relative flex h-10 w-10 items-center justify-center overflow-hidden rounded-lg border-2 border-input text-sm transition-all",
                disabled && "cursor-not-allowed opacity-50",
                index === 0 && "rounded-l-lg",
                index === maxLength - 1 && "rounded-r-lg"
              )}
            >
              <span className="text-base font-semibold leading-none">{digit}</span>
            </div>
          ))}
        </div>

        <input
          type="text"
          inputMode="numeric"
          autoComplete="one-time-code"
          autoFocus={autoFocus}
          disabled={disabled}
          value={digits}
          maxLength={maxLength}
          onChange={handleChange}
          className="absolute inset-0 h-full w-full cursor-text opacity-0"
          aria-label="Code de vérification"
        />
      </div>
    )
  }
)
InputOTP.displayName = "InputOTP"

const InputOTPGroup = React.forwardRef<
  React.ElementRef<"div">,
  React.ComponentPropsWithoutRef<"div">
>(({ className, ...props }, ref) => (
  <div ref={ref} className={cn("flex items-center", className)} {...props} />
))
InputOTPGroup.displayName = "InputOTPGroup"

const InputOTPSlot = React.forwardRef<
  React.ElementRef<"div">,
  React.ComponentPropsWithoutRef<"div"> & { index: number }
>(({ className, ...props }, ref) => (
  <div ref={ref} className={cn("flex h-10 w-10 items-center justify-center", className)} {...props} />
))
InputOTPSlot.displayName = "InputOTPSlot"

const InputOTPSeparator = React.forwardRef<
  React.ElementRef<"div">,
  React.ComponentPropsWithoutRef<"div">
>(({ ...props }, ref) => (
  <div ref={ref} role="separator" {...props}>
    <Dot />
  </div>
))
InputOTPSeparator.displayName = "InputOTPSeparator"

export { InputOTP, InputOTPGroup, InputOTPSlot, InputOTPSeparator }
